import React, { useState, useCallback, useEffect, useMemo } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import { IoMdClose } from "react-icons/io";
import { MdKeyboardArrowDown, MdKeyboardArrowUp } from "react-icons/md";
import { BudgetService } from "../../../../services/budget.service";
import { BudgetInfoComparison } from "../../../../types/budget/comparison-types";
import {
  buildComparisonCategoryTotalRows,
  getComparisonServiceValorTotalDisplay,
  getComparisonServiceValorUnitarioDisplay,
  getComparisonSummaryTaxFlags,
  getExportParityCategorySummaryTotalBrl,
  getExportParityCategorySummaryVu,
  resolveComparisonServiceValues as resolveServiceValues,
  type ComparisonCategoryTotalRowDef,
} from "../../../../utils/budgetComparisonTaxDisplay";
import { BudgetItemTypeEnum } from "../../../../types/budget/budget";
import { ServiceOptions } from "../../../../utils/serviceTypesFromBudget";
import {
  comparisonBudgetDisplayName,
  comparisonBudgetVersionHistory,
  findComparisonBudget,
  type ComparisonModalBudgetList,
} from "../comparison-modal-list";
import { ObsCell } from "../ObsCell";

const C = {
  sectionBg: "#3b668b",
  grandTotalBg: "#274966",
  categoryBg: "#f8f8f8",
  separatorBg: "#e0e0e0",
  border: "#e0e0e0",
  cellText: "#222",
  titleText: "#4b465c",
  descText: "#6f6b7d",
  primary: "#1361a4",
  linkBlue: "#1976d2",
  controlBorder: "#c4c4c4",
  white: "#ffffff",
  budgetCardBg: "#fcfcfc",
  budgetCardBorder: "#e8e8e8",
  savingsNeg: "#D32F2F",
  savingsPos: "#2e7d32",
};

const FONT = "'Public Sans', sans-serif";
const bdr = `1px solid ${C.border}`;
/** Divisória vertical entre cada bloco de orçamento (igual comparativo orçamento / negociação). */
const GROUP_DIVIDER_R = "3px solid #7A909F";
/** Linhas internas nas faixas azul / total escuro */
const SECTION_INNER_R = "1px solid rgba(255,255,255,0.32)";

const LABEL_W = 320;
const HEADER_H = 48;
/** Mesmo grid da modal de negociação (meio-termo largura × legibilidade) + colunas de Descrição/Obs. Internas. */
const COL_WIDTHS = [200, 62, 88, 158, 180, 168, 220];
/** Índices fixos das subcolunas dentro de cada bloco de orçamento. */
const IDX_DESCRICAO = 0;
const IDX_TOTAL = 4;
const IDX_SAVINGS = 5;
const IDX_OBS = 6;

function budgetBlockCellBorderRight(ci: number): string {
  if (ci < COL_WIDTHS.length - 1) return bdr;
  /** Sempre divisória forte após a 5ª subcoluna: entre orçamentos e antes de Diferença Total. */
  return GROUP_DIVIDER_R;
}

function budgetSectionInnerBorderRight(ci: number): string {
  if (ci < COL_WIDTHS.length - 1) return SECTION_INNER_R;
  return GROUP_DIVIDER_R;
}

const COL_LABELS_BASE = ["Qtde", "Diárias", "Valor Unitário", "Valor Total"];
const OBS_LABEL = "Obs. Internas";
const BUDGET_W_COLS = COL_WIDTHS.reduce((a, b) => a + b, 0);
const DIFF_TOTAL_W = 168;

const ROW_H: Record<string, number> = {
  "section-header": 48,
  "category-header": 40,
  "column-header": 36,
  service: 32,
  separator: 4,
  "category-total": 32,
  "section-total": 40,
  "grand-total": 48,
};

const INFO_ROWS = [
  { key: "local", label: "LOCAL" },
  { key: "city", label: "CIDADE/UF" },
  { key: "country", label: "PAÍS" },
  { key: "address", label: "ENDEREÇO" },
  { key: "eventPeriods", label: "PERÍODO DE HOSPEDAGEM" },
  { key: "meetingPeriod", label: "PERÍODO DE REUNIÃO" },
  { key: "availability", label: "DISPONIBILIDADE" },
  { key: "deadline", label: "DEADLINE" },
  { key: "site", label: "SITE" },
];

function savingsStyle(savingsRaw: number): { color: string; bgcolor: string } {
  return savingsRaw > 0
    ? { color: C.savingsNeg, bgcolor: "rgba(211,47,47,0.06)" }
    : { color: C.savingsPos, bgcolor: "rgba(46,125,50,0.06)" };
}

function totalStyle(
  current: number,
  base: number
): { color: string; bgcolor: string } {
  if (!current || !base || current === base)
    return { color: C.cellText, bgcolor: "transparent" };
  return current > base
    ? { color: C.savingsNeg, bgcolor: "rgba(211,47,47,0.05)" }
    : { color: C.savingsPos, bgcolor: "rgba(46,125,50,0.05)" };
}

/** Assessoria não entra em totais; fallback por nome se a API não mandar isConsulting. */
function isConsultingColumn(bd: BudgetInfoComparison): boolean {
  if (bd.isConsulting) return true;
  return (bd.budgetName || "").toLowerCase().includes("assessoria");
}

/**
 * Só a coluna “Realizado” usa o rótulo “Diferença” na 5ª subcoluna.
 * Na API (generateComparisonInfo): Orçamento = OPEN/v0, Aprovado = NEGOTIATE, Realizado = APPROVED, Assessoria = NEGOTIATE+consulting.
 * Não usar OPEN/v0 aqui — isso é o bloco “Orçamento”, que deve ser “Savings” como Aprovado e Assessoria.
 */
function isRealizadoColumn(bd: BudgetInfoComparison): boolean {
  const st = (bd.status || "").toUpperCase();
  if (st === "APPROVED" || st === "APROVED") return true;
  return (bd.budgetName || "").toLowerCase().includes("realizado");
}

function savingsSubcolumnLabel(bd: BudgetInfoComparison): string {
  return isRealizadoColumn(bd) ? "Diferença" : "Savings";
}

/** Orçamento base primeiro, fluxo por versão, assessoria sempre por último antes de Diferença Total. */
function sortClosingCompareColumns(
  data: BudgetInfoComparison[]
): BudgetInfoComparison[] {
  return [...data].sort((a, b) => {
    const aOpen = (a.status || "").toUpperCase() === "OPEN" || a.version === 0;
    const bOpen = (b.status || "").toUpperCase() === "OPEN" || b.version === 0;
    if (aOpen !== bOpen) return aOpen ? -1 : 1;
    const aCon = isConsultingColumn(a);
    const bCon = isConsultingColumn(b);
    if (aCon !== bCon) return aCon ? 1 : -1;
    return a.version - b.version;
  });
}

type BudgetConfig = {
  budgetId: string;
  budgetName: string;
  budgetIndex: number;
  selected: boolean;
  hasConsultingVersion: boolean;
};

type TabState = {
  budgetId: string;
  budgetName: string;
  budgetIndex: number;
  includeConsulting: boolean;
  loading: boolean;
  error: string | null;
  data: BudgetInfoComparison[] | null;
  collSec: Set<string>;
  collCat: Set<string>;
};

type TableRow =
  | { kind: "info-row"; fieldKey: string; label: string }
  | { kind: "section-header"; sectionKey: string; label: string }
  | {
      kind: "category-header";
      sectionKey: string;
      categoryKey: string;
      label: string;
    }
  | { kind: "column-header"; sectionKey: string; categoryKey: string }
  | {
      kind: "service";
      sectionKey: string;
      categoryKey: string;
      serviceType: string;
      slot: number;
      label: string;
    }
  | { kind: "separator"; sectionKey: string; categoryKey: string }
  | {
      kind: "category-total";
      sectionKey: string;
      categoryKey: string;
      totalKey: string;
      label: string;
      isLast: boolean;
    }
  | { kind: "section-total"; sectionKey: string; label: string }
  | { kind: "grand-total"; label: string }
  | { kind: "gap"; height: number; sectionKey?: string; inSection: boolean };

interface Props {
  isOpen: boolean;
  onClose: () => void;
  /** List/summary rows for selection labels + versionHistory — payloads from export API by id. */
  budgetList: ComparisonModalBudgetList;
}

const fmtCurrency = (v?: number | null) => {
  if (v === null || v === undefined || isNaN(v as number)) return "-";
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(v as number);
};

const getCatLabel = (key: string) =>
  BudgetItemTypeEnum[key as keyof typeof BudgetItemTypeEnum] || key;

function buildRows(
  data: BudgetInfoComparison[],
  catTotals: ComparisonCategoryTotalRowDef[]
): TableRow[] {
  const rows: TableRow[] = [];
  if (!data?.length) return rows;

  INFO_ROWS.forEach(({ key, label }) =>
    rows.push({ kind: "info-row", fieldKey: key, label })
  );
  rows.push({ kind: "gap", height: 16, inSection: false });

  const sectionOrder: string[] = [];
  const sectionTitles = new Map<string, string>();
  const catsBySection = new Map<string, string[]>();

  for (const b of data) {
    for (const sec of b.sections || []) {
      if (!sectionTitles.has(sec.key)) {
        sectionOrder.push(sec.key);
        sectionTitles.set(sec.key, sec.title);
        catsBySection.set(sec.key, []);
      }
      for (const svc of sec.allServices || []) {
        const cats = catsBySection.get(sec.key)!;
        if (!cats.includes(svc.type)) cats.push(svc.type);
      }
    }
  }

  sectionOrder.sort((a, b) => a.localeCompare(b));

  for (const sk of sectionOrder) {
    rows.push({
      kind: "section-header",
      sectionKey: sk,
      label: sectionTitles.get(sk) || sk,
    });
    rows.push({ kind: "gap", height: 8, sectionKey: sk, inSection: true });

    const cats = catsBySection.get(sk) || [];
    cats.forEach((ck, ci) => {
      if (ci > 0)
        rows.push({ kind: "gap", height: 8, sectionKey: sk, inSection: true });
      rows.push({
        kind: "category-header",
        sectionKey: sk,
        categoryKey: ck,
        label: `${ci + 1}   ${getCatLabel(ck)}`,
      });
      rows.push({ kind: "column-header", sectionKey: sk, categoryKey: ck });

      const seenTypes = new Set<string>();
      const orderedTypes: string[] = [];
      for (const b of data) {
        const sec = b.sections?.find((s) => s.key === sk);
        for (const svc of sec?.allServices?.filter((sv) => sv.type === ck) ||
          []) {
          const st = resolveServiceValues(svc, b.status, b.version)
            ?.serviceType as string | undefined;
          if (st && !seenTypes.has(st)) {
            seenTypes.add(st);
            orderedTypes.push(st);
          }
        }
      }

      let globalIdx = 0;
      for (const st of orderedTypes) {
        let maxCount = 0;
        for (const b of data) {
          const sec = b.sections?.find((s) => s.key === sk);
          const cnt =
            sec?.allServices?.filter(
              (sv) =>
                sv.type === ck &&
                resolveServiceValues(sv, b.status, b.version)?.serviceType ===
                  st
            ).length ?? 0;
          if (cnt > maxCount) maxCount = cnt;
        }
        const displayName =
          (ServiceOptions as any)[ck]?.find((o: any) => o.value === st)?.text ??
          st;
        for (let slot = 0; slot < maxCount; slot++) {
          rows.push({
            kind: "service",
            sectionKey: sk,
            categoryKey: ck,
            serviceType: st,
            slot,
            label: `${globalIdx + 1} ${displayName}`,
          });
          globalIdx++;
        }
      }

      rows.push({ kind: "separator", sectionKey: sk, categoryKey: ck });
      catTotals.forEach((t, ti) =>
        rows.push({
          kind: "category-total",
          sectionKey: sk,
          categoryKey: ck,
          totalKey: t.key,
          label: t.label,
          isLast: ti === catTotals.length - 1,
        })
      );
    });

    rows.push({ kind: "gap", height: 8, sectionKey: sk, inSection: true });
    rows.push({
      kind: "section-total",
      sectionKey: sk,
      label: `[${sk}] TOTAL`,
    });
    rows.push({ kind: "gap", height: 32, sectionKey: sk, inSection: false });
  }

  rows.push({
    kind: "grand-total",
    label: "RESUMO GERAL DOS CUSTOS (A+B+C+D)",
  });
  return rows;
}

type FiveVals = [
  string,
  string | number,
  string | number,
  string | number,
  string | number,
  string | number,
  string
];

function getInfoValue(b: BudgetInfoComparison, fieldKey: string): string {
  const ci = b.comparisonInfo as any;
  if (fieldKey === "eventPeriods") {
    const periods = ci?.eventPeriods as
      | Array<{ inicio: string; fim: string }>
      | undefined;
    if (periods?.length) {
      return periods
        .map((p) => {
          try {
            const fmt = (iso: string) =>
              new Date(iso).toLocaleString("pt-BR", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              });
            return `${fmt(p.inicio)} à ${fmt(p.fim)}`;
          } catch {
            return `${p.inicio} à ${p.fim}`;
          }
        })
        .join(" | ");
    }
    return ci?.accommodationPeriod || "-";
  }
  return ci?.[fieldKey] || "-";
}

function getValues(
  b: BudgetInfoComparison,
  row: TableRow,
  columnData: BudgetInfoComparison[],
  bdIndex: number,
  taxFlags: { showIssSummary: boolean; showIofSummary: boolean }
): FiveVals {
  const savingsFmt = (isServiceSpecial?: boolean) => {
    if (b.version === 0) return fmtCurrency(0);
    if (isServiceSpecial) return "-";
    // Assessoria: mostrar delta vs coluna anterior (igual Excel); não entra só em sumSavingsForDiffTotal.
    return fmtCurrency(getSavingsRaw(columnData, bdIndex, row, taxFlags));
  };

  if (row.kind === "info-row") {
    return ["", "", "", "", getInfoValue(b, row.fieldKey), "", ""];
  }
  if (row.kind === "section-header") {
    const s = b.sections?.find((x) => x.key === row.sectionKey);
    return [
      "",
      "-",
      "-",
      "-",
      fmtCurrency((s?.sectionTotals as any)?.totals?.totalInBRL),
      savingsFmt(),
      "",
    ];
  }
  if (row.kind === "service") {
    const s = b.sections?.find((x) => x.key === row.sectionKey);
    const sameType =
      s?.allServices?.filter(
        (x) =>
          x.type === row.categoryKey &&
          resolveServiceValues(x, b.status, b.version)?.serviceType ===
            row.serviceType
      ) ?? [];
    const svc = sameType[row.slot];
    if (!svc)
      return ["-", 0, 0, fmtCurrency(0), fmtCurrency(0), savingsFmt(), "-"];
    const sv = resolveServiceValues(svc, b.status, b.version);
    const typeTax = sv?.taxData?.tariff?.typeTax;
    const isCortesy = typeTax === "CORTESY";
    const isIncluso = typeTax === "INCLUDED";
    const isAcClient = typeTax === "AC_CLIENT";
    const valueText = isCortesy
      ? "Cortesia"
      : isIncluso
      ? "Incluso"
      : "A/C Cliente";
    const vuCell = getComparisonServiceValorUnitarioDisplay(sv, true);
    const valorUn =
      isCortesy || isIncluso || isAcClient
        ? valueText
        : typeof vuCell === "number"
        ? fmtCurrency(vuCell)
        : vuCell;
    const vtCell = getComparisonServiceValorTotalDisplay(sv, true);
    const valorTotal =
      isCortesy || isIncluso || isAcClient
        ? valueText
        : typeof vtCell === "number" && Number.isFinite(vtCell)
        ? fmtCurrency(vtCell)
        : typeof vtCell === "string"
        ? vtCell
        : fmtCurrency(0);
    // Descrição do serviço (campo voltado ao cliente) — igual à coluna
    // "Descrição" do Excel comparativo; distinta de "Obs. Internas".
    return [
      sv?.observation || "-",
      sv?.quantity ?? 0,
      sv?.daily ?? 0,
      valorUn,
      valorTotal,
      savingsFmt(isCortesy || isIncluso || isAcClient),
      sv?.internalObservation || "-",
    ];
  }
  if (row.kind === "category-total") {
    const s = b.sections?.find((x) => x.key === row.sectionKey);
    const ct = (s?.totalsPerCategory as any)?.[row.categoryKey];
    const vuRaw = getExportParityCategorySummaryVu(
      row.totalKey,
      b,
      row.sectionKey,
      row.categoryKey
    );
    const valorUn = vuRaw === "" ? "" : vuRaw;
    const vt = getExportParityCategorySummaryTotalBrl(
      row.totalKey,
      ct,
      taxFlags
    );
    return ["", "-", "-", valorUn, fmtCurrency(vt), savingsFmt(), ""];
  }
  if (row.kind === "section-total") {
    const s = b.sections?.find((x) => x.key === row.sectionKey);
    return [
      "",
      "-",
      "-",
      "-",
      fmtCurrency((s?.sectionTotals as any)?.totals?.totalInBRL),
      savingsFmt(),
      "",
    ];
  }
  if (row.kind === "grand-total") {
    return [
      "",
      "-",
      "-",
      "-",
      fmtCurrency((b.totals as any)?.totals?.totals?.totalInBRL),
      savingsFmt(),
      "",
    ];
  }
  return ["", "", "", "", "", "", ""];
}

function getTotalRaw(
  bd: BudgetInfoComparison,
  row: TableRow,
  taxFlags: { showIssSummary: boolean; showIofSummary: boolean }
): number {
  if (row.kind === "section-header") {
    const s = bd.sections?.find((x) => x.key === row.sectionKey);
    return (s?.sectionTotals as any)?.totals?.totalInBRL ?? 0;
  }
  if (row.kind === "service") {
    const s = bd.sections?.find((x) => x.key === row.sectionKey);
    const sameType =
      s?.allServices?.filter(
        (x) =>
          x.type === row.categoryKey &&
          resolveServiceValues(x, bd.status, bd.version)?.serviceType ===
            row.serviceType
      ) ?? [];
    const svc = sameType[row.slot];
    if (!svc) return 0;
    const sv = resolveServiceValues(svc, bd.status, bd.version);
    const typeTax = sv?.taxData?.tariff?.typeTax;
    if (
      typeTax === "CORTESY" ||
      typeTax === "INCLUDED" ||
      typeTax === "AC_CLIENT"
    )
      return 0;
    const vt = getComparisonServiceValorTotalDisplay(sv, true);
    return typeof vt === "number" && Number.isFinite(vt) ? vt : 0;
  }
  if (row.kind === "category-total") {
    const s = bd.sections?.find((x) => x.key === row.sectionKey);
    const ct = (s?.totalsPerCategory as any)?.[row.categoryKey];
    return getExportParityCategorySummaryTotalBrl(row.totalKey, ct, taxFlags);
  }
  if (row.kind === "section-total") {
    const s = bd.sections?.find((x) => x.key === row.sectionKey);
    return (s?.sectionTotals as any)?.totals?.totalInBRL ?? 0;
  }
  if (row.kind === "grand-total") {
    return (bd.totals as any)?.totals?.totals?.totalInBRL ?? 0;
  }
  return 0;
}

/** Orçamento base (coluna 0 no Excel) — referência do Saving da Assessoria. */
function compareBaselineBudget(
  columnData: BudgetInfoComparison[]
): BudgetInfoComparison | undefined {
  return columnData.find(
    (b) => b.version === 0 || (b.status || "").toUpperCase() === "OPEN"
  );
}

/** Igual export-comparative-sheet: assessoria compara com Orçamento[0], demais com coluna anterior. */
function previousBudgetForSavings(
  columnData: BudgetInfoComparison[],
  bdIndex: number
): BudgetInfoComparison | undefined {
  const bd = columnData[bdIndex];
  if (isConsultingColumn(bd)) return compareBaselineBudget(columnData);
  return bdIndex > 0 ? columnData[bdIndex - 1] : undefined;
}

function getSavingsRaw(
  columnData: BudgetInfoComparison[],
  bdIndex: number,
  row: TableRow,
  taxFlags: { showIssSummary: boolean; showIofSummary: boolean }
): number {
  if (bdIndex <= 0) return 0;
  const bd = columnData[bdIndex];
  if (bd.version === 0) return 0;
  const prev = previousBudgetForSavings(columnData, bdIndex);
  if (!prev) return 0;
  if (row.kind === "service") {
    const s = bd.sections?.find((x) => x.key === row.sectionKey);
    const sameType =
      s?.allServices?.filter(
        (x) =>
          x.type === row.categoryKey &&
          resolveServiceValues(x, bd.status, bd.version)?.serviceType ===
            row.serviceType
      ) ?? [];
    const svc = sameType[row.slot];
    if (!svc)
      return getTotalRaw(bd, row, taxFlags) - getTotalRaw(prev, row, taxFlags);
    const sv = resolveServiceValues(svc, bd.status, bd.version);
    const typeTax = sv?.taxData?.tariff?.typeTax;
    if (
      typeTax === "CORTESY" ||
      typeTax === "INCLUDED" ||
      typeTax === "AC_CLIENT"
    )
      return 0;
  }
  return getTotalRaw(bd, row, taxFlags) - getTotalRaw(prev, row, taxFlags);
}

/** Diferença Total: soma só colunas do fluxo (assessoria tem célula Savings igual ao Excel, mas não soma aqui). */
function sumSavingsForDiffTotal(
  columnData: BudgetInfoComparison[],
  row: TableRow,
  taxFlags: { showIssSummary: boolean; showIofSummary: boolean }
): number {
  return columnData.reduce((sum, bd, bi) => {
    if (isConsultingColumn(bd)) return sum;
    return sum + getSavingsRaw(columnData, bi, row, taxFlags);
  }, 0);
}

function isRowVisible(
  row: TableRow,
  collSec: Set<string>,
  collCat: Set<string>
): boolean {
  if (
    row.kind === "grand-total" ||
    row.kind === "section-header" ||
    row.kind === "info-row"
  )
    return true;
  if (row.kind === "column-header") {
    const sk = row.sectionKey;
    if (sk && collSec.has(sk)) return false;
    const ck = row.categoryKey;
    if (sk && ck && collCat.has(`${sk}||${ck}`)) return false;
    return true;
  }
  if (row.kind === "gap") {
    if (!row.inSection) return true;
    return !(row.sectionKey && collSec.has(row.sectionKey));
  }
  const sk = (row as any).sectionKey as string | undefined;
  if (sk && collSec.has(sk)) return false;
  if (row.kind === "category-header") return true;
  const ck = (row as any).categoryKey as string | undefined;
  if (sk && ck && collCat.has(`${sk}||${ck}`)) return false;
  return true;
}

/** API envia budgetName = sheetTitle (ex.: "Orçamento X - Realizado"). */
function getClosingColLabel(b: BudgetInfoComparison): string {
  return b.budgetName || "Orçamento";
}

const ClosingComparisonModal: React.FC<Props> = ({
  isOpen,
  onClose,
  budgetList,
}) => {
  const [step, setStep] = useState<"selection" | "comparison">("selection");
  const [configs, setConfigs] = useState<BudgetConfig[]>([]);
  const [includeConsulting, setIncludeConsulting] = useState(false);
  const [tabs, setTabs] = useState<TabState[]>([]);
  const [activeTab, setActiveTab] = useState(0);
  const [advancing, setAdvancing] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setStep("selection");
      setConfigs([]);
      setIncludeConsulting(false);
      setTabs([]);
      setActiveTab(0);
      setAdvancing(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const built: BudgetConfig[] = [];
    (budgetList || []).forEach((budget, bi) => {
      const hasConsultingVersion = comparisonBudgetVersionHistory(budget).some(
        (vh) => vh.isConsulting
      );
      built.push({
        budgetId: budget._id || "",
        budgetName: comparisonBudgetDisplayName(budget, bi),
        budgetIndex: budget.budgetIndex ?? bi,
        selected: false,
        hasConsultingVersion,
      });
    });
    setConfigs(built);
  }, [isOpen, budgetList]);

  const selectedCount = useMemo(
    () => configs.filter((c) => c.selected).length,
    [configs]
  );

  const toggleConfig = (budgetId: string) => {
    setConfigs((prev) =>
      prev.map((c) =>
        c.budgetId === budgetId ? { ...c, selected: !c.selected } : c
      )
    );
  };

  const resetSelection = () => {
    setConfigs((prev) => prev.map((c) => ({ ...c, selected: false })));
    setIncludeConsulting(false);
  };

  const fetchTabData = useCallback(
    async (tab: TabState, updater: (patch: Partial<TabState>) => void) => {
      updater({ loading: true, error: null });
      try {
        const exportType = tab.includeConsulting
          ? "CLIENT_CONSULTING"
          : "CLIENT";
        const data = await BudgetService.generateClosingComparisonInfo({
          exportType,
          sortOrder: "DEFAULT",
          budgetId: tab.budgetId,
        });
        updater({ data: data || [], loading: false });
      } catch {
        updater({
          error: "Não foi possível carregar a comparação. Tente novamente.",
          loading: false,
        });
      }
    },
    []
  );

  const handleAdvance = async () => {
    if (selectedCount === 0) return;
    setAdvancing(true);
    const initialTabs: TabState[] = configs
      .filter((c) => c.selected)
      .map((c) => ({
        budgetId: c.budgetId,
        budgetName: c.budgetName,
        budgetIndex: c.budgetIndex,
        includeConsulting,
        loading: false,
        error: null,
        data: null,
        collSec: new Set<string>(),
        collCat: new Set<string>(),
      }));
    setTabs(initialTabs);
    setActiveTab(0);
    setStep("comparison");
    setAdvancing(false);
    initialTabs.forEach((tab, i) => {
      fetchTabData(tab, (patch) => {
        setTabs((prev) =>
          prev.map((t, ti) => (ti === i ? { ...t, ...patch } : t))
        );
      });
    });
  };

  const updateTab = (tabIndex: number, patch: Partial<TabState>) => {
    setTabs((prev) =>
      prev.map((t, i) => (i === tabIndex ? { ...t, ...patch } : t))
    );
  };

  const refreshTab = (tabIndex: number, tab: TabState) => {
    fetchTabData(tab, (patch) => {
      setTabs((prev) =>
        prev.map((t, i) => (i === tabIndex ? { ...t, ...patch } : t))
      );
    });
  };

  const handleToggleConsulting = (tabIndex: number, checked: boolean) => {
    const updated = { ...tabs[tabIndex], includeConsulting: checked };
    updateTab(tabIndex, { includeConsulting: checked });
    refreshTab(tabIndex, updated);
  };

  const handleRetry = (tabIndex: number) => {
    refreshTab(tabIndex, tabs[tabIndex]);
  };

  const handleAddBudgetTab = useCallback(
    (budgetId: string) => {
      const b = findComparisonBudget(budgetList, budgetId);
      if (!b) return;
      setTabs((prev) => {
        if (prev.some((t) => t.budgetId === budgetId)) return prev;
        const i = prev.length;
        const newTab: TabState = {
          budgetId: b._id || "",
          budgetName: comparisonBudgetDisplayName(b),
          budgetIndex: b.budgetIndex ?? 0,
          includeConsulting: false,
          loading: false,
          error: null,
          data: null,
          collSec: new Set<string>(),
          collCat: new Set<string>(),
        };
        fetchTabData(newTab, (patch) => {
          setTabs((p2) =>
            p2.map((t, ti) => (ti === i ? { ...t, ...patch } : t))
          );
        });
        setTimeout(() => setActiveTab(i), 0);
        return [...prev, newTab];
      });
    },
    [budgetList, fetchTabData]
  );

  /** Igual NegotiationComparisonModal: backdrop/Escape não alteram `open`; só botões/X chamam onClose. */
  const blockDialogDismiss = useCallback(() => {}, []);

  return (
    <>
      <Dialog
        open={isOpen && step === "selection"}
        onClose={blockDialogDismiss}
        disableEscapeKeyDown
        maxWidth={false}
        PaperProps={{
          sx: {
            width: 680,
            maxWidth: "95vw",
            borderRadius: "8px",
            boxShadow: "0px 4px 18px rgba(75,70,92,0.1)",
          },
        }}
      >
        <SelectionStep
          configs={configs}
          includeConsulting={includeConsulting}
          onToggle={toggleConfig}
          onToggleConsulting={setIncludeConsulting}
          onReset={resetSelection}
          onCancel={onClose}
          onAdvance={handleAdvance}
          advancing={advancing}
          selectedCount={selectedCount}
        />
      </Dialog>
      <Dialog
        open={isOpen && step === "comparison"}
        onClose={blockDialogDismiss}
        disableEscapeKeyDown
        maxWidth={false}
        sx={{
          "& .MuiDialog-paper": { margin: "56px" },
          "& .MuiDialog-container": { padding: 0 },
        }}
        PaperProps={{
          sx: {
            width: "calc(100% - 112px)",
            maxWidth: "calc(100% - 112px)",
            height: "calc(100% - 112px)",
            maxHeight: "calc(100% - 112px)",
            borderRadius: "8px",
            boxShadow: "0px 4px 18px rgba(75,70,92,0.1)",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          },
        }}
      >
        <ComparisonStep
          tabs={tabs}
          activeTab={activeTab}
          budgetList={budgetList}
          onChangeTab={(_, v) => setActiveTab(v)}
          onToggleConsulting={handleToggleConsulting}
          onRetry={handleRetry}
          onBack={() => setStep("selection")}
          onClose={onClose}
          updateTab={updateTab}
          onAddBudgetTab={handleAddBudgetTab}
        />
      </Dialog>
    </>
  );
};

interface SelectionStepProps {
  configs: BudgetConfig[];
  includeConsulting: boolean;
  onToggle: (budgetId: string) => void;
  onToggleConsulting: (checked: boolean) => void;
  onReset: () => void;
  onCancel: () => void;
  onAdvance: () => void;
  advancing: boolean;
  selectedCount: number;
}

const SelectionStep: React.FC<SelectionStepProps> = ({
  configs,
  includeConsulting,
  onToggle,
  onToggleConsulting,
  onReset,
  onCancel,
  onAdvance,
  advancing,
  selectedCount,
}) => {
  const consultingAvailable = useMemo(
    () => configs.filter((c) => c.selected).some((c) => c.hasConsultingVersion),
    [configs]
  );

  useEffect(() => {
    if (!consultingAvailable && includeConsulting) onToggleConsulting(false);
  }, [consultingAvailable, includeConsulting, onToggleConsulting]);

  return (
    <Box sx={{ p: 4, display: "flex", flexDirection: "column", gap: 4 }}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 4 }}>
          <Typography
            sx={{
              flex: 1,
              fontFamily: FONT,
              fontSize: 24,
              fontWeight: 400,
              color: C.titleText,
              lineHeight: "32px",
            }}
          >
            Pré-visualizar fechamentos
          </Typography>
          <IconButton
            size="small"
            onClick={onCancel}
            sx={{ color: C.titleText }}
          >
            <IoMdClose size={20} />
          </IconButton>
        </Box>
        <Typography
          sx={{
            fontFamily: FONT,
            fontSize: 15,
            fontWeight: 400,
            color: C.descText,
            lineHeight: "22px",
          }}
        >
          Selecione abaixo os orçamentos desejados. Cada orçamento terá sua
          própria aba de comparativos.
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 0.5,
          maxHeight: "55vh",
          overflowY: "auto",
          pr: 0.5,
          scrollbarWidth: "thin",
          "&::-webkit-scrollbar": { width: 6 },
          "&::-webkit-scrollbar-thumb": {
            background: "rgba(0,0,0,0.2)",
            borderRadius: 3,
          },
        }}
      >
        {configs.length === 0 ? (
          <Typography
            sx={{
              color: C.descText,
              fontSize: 14,
              textAlign: "center",
              py: 3,
              fontFamily: FONT,
            }}
          >
            Nenhum orçamento disponível.
          </Typography>
        ) : (
          <Box
            sx={{
              border: `1px solid ${C.border}`,
              borderRadius: "8px",
              overflow: "hidden",
              bgcolor: C.budgetCardBg,
            }}
          >
            {configs.map((cfg, idx) => (
              <FormControlLabel
                key={cfg.budgetId}
                control={
                  <Checkbox
                    checked={cfg.selected}
                    onChange={() => onToggle(cfg.budgetId)}
                    size="small"
                    sx={{
                      py: 0.5,
                      pl: 1.5,
                      "& .MuiSvgIcon-root": { fontSize: 20, color: C.primary },
                    }}
                  />
                }
                label={
                  <Typography
                    sx={{ fontFamily: FONT, fontSize: 14, color: C.cellText }}
                  >
                    {cfg.budgetName}
                  </Typography>
                }
                sx={{
                  m: 0,
                  display: "flex",
                  width: "100%",
                  px: 1,
                  py: 0.25,
                  borderTop:
                    idx > 0 ? `1px solid ${C.budgetCardBorder}` : "none",
                  "&:hover": { bgcolor: "rgba(19,97,164,0.04)" },
                }}
              />
            ))}
          </Box>
        )}
      </Box>

      <Tooltip
        title={
          !consultingAvailable && selectedCount > 0
            ? "Nenhum dos orçamentos selecionados possui versão de assessoria"
            : ""
        }
      >
        <FormControlLabel
          control={
            <Checkbox
              checked={includeConsulting}
              disabled={!consultingAvailable}
              onChange={(e) => onToggleConsulting(e.target.checked)}
              size="small"
              sx={{
                p: 0,
                mr: 1,
                "& .MuiSvgIcon-root": { fontSize: 18, color: C.primary },
              }}
            />
          }
          label={
            <Typography
              sx={{
                fontFamily: FONT,
                fontSize: 14,
                color: consultingAvailable ? C.cellText : C.descText,
              }}
            >
              Incluir versão assessoria
            </Typography>
          }
          sx={{
            m: 0,
            opacity: consultingAvailable || selectedCount === 0 ? 1 : 0.55,
          }}
        />
      </Tooltip>

      <Box
        sx={{
          display: "flex",
          gap: 2,
          justifyContent: "flex-end",
          alignItems: "center",
        }}
      >
        <Button
          variant="text"
          onClick={onReset}
          sx={{
            fontFamily: FONT,
            fontWeight: 600,
            color: C.linkBlue,
            fontSize: 14,
            textTransform: "none",
          }}
        >
          Redefinir seleção
        </Button>
        <Button
          variant="outlined"
          onClick={onCancel}
          sx={{
            fontFamily: FONT,
            fontWeight: 600,
            fontSize: 14,
            textTransform: "none",
            borderColor: C.primary,
            color: C.linkBlue,
            borderRadius: "8px",
            px: 1.5,
            height: 40,
          }}
        >
          Cancelar
        </Button>
        <Button
          variant="contained"
          disabled={selectedCount === 0 || advancing}
          onClick={onAdvance}
          startIcon={
            advancing ? (
              <CircularProgress size={16} color="inherit" />
            ) : undefined
          }
          sx={{
            fontFamily: FONT,
            fontWeight: 600,
            fontSize: 14,
            textTransform: "none",
            bgcolor: C.primary,
            borderRadius: "8px",
            px: 1.5,
            height: 40,
            "&:hover": { bgcolor: "#0f4f89" },
          }}
        >
          Avançar
        </Button>
      </Box>
    </Box>
  );
};

interface ComparisonStepProps {
  tabs: TabState[];
  activeTab: number;
  budgetList: ComparisonModalBudgetList;
  onChangeTab: (event: React.SyntheticEvent, value: number) => void;
  onToggleConsulting: (tabIndex: number, checked: boolean) => void;
  onRetry: (tabIndex: number) => void;
  onBack: () => void;
  onClose: () => void;
  updateTab: (tabIndex: number, patch: Partial<TabState>) => void;
  onAddBudgetTab: (budgetId: string) => void;
}

const ComparisonStep: React.FC<ComparisonStepProps> = ({
  tabs,
  activeTab,
  budgetList,
  onChangeTab,
  onToggleConsulting,
  onRetry,
  onBack,
  onClose,
  updateTab,
  onAddBudgetTab,
}) => {
  const tab = tabs[activeTab];
  const [addBudgetValue, setAddBudgetValue] = useState("");

  const tabHasConsulting = useMemo(() => {
    if (!tab) return false;
    return comparisonBudgetVersionHistory(
      findComparisonBudget(budgetList, tab.budgetId)
    ).some((vh) => vh.isConsulting);
  }, [tab, budgetList]);

  const budgetsToAdd = useMemo(() => {
    const inTabs = new Set(tabs.map((t) => t.budgetId));
    return (budgetList || []).filter((b) => b._id && !inTabs.has(b._id));
  }, [budgetList, tabs]);

  return (
    <>
      <Box sx={{ px: 4, pt: 4, pb: 0, flexShrink: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <Typography
            sx={{
              flex: 1,
              fontFamily: FONT,
              fontSize: 24,
              fontWeight: 400,
              color: C.titleText,
              lineHeight: "32px",
            }}
          >
            Pré-visualizar fechamentos
          </Typography>
          <IconButton
            size="small"
            onClick={onClose}
            sx={{ color: C.titleText }}
          >
            <IoMdClose size={20} />
          </IconButton>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "flex-end",
            gap: 2,
            flexWrap: "wrap",
            borderBottom: bdr,
            pb: 0,
          }}
        >
          <Tabs
            value={activeTab}
            onChange={onChangeTab}
            sx={{
              flex: 1,
              minWidth: 0,
              minHeight: 48,
              "& .MuiTab-root": {
                textTransform: "none",
                fontFamily: FONT,
                fontSize: 14,
                color: C.titleText,
                minHeight: 48,
              },
              "& .Mui-selected": { color: C.primary, fontWeight: 600 },
              "& .MuiTabs-indicator": { bgcolor: C.primary },
            }}
          >
            {tabs.map((t, i) => (
              <Tab key={t.budgetId} label={t.budgetName} value={i} />
            ))}
          </Tabs>
          {budgetsToAdd.length > 0 && (
            <FormControl
              size="small"
              sx={{ minWidth: 220, mb: 0.5, flexShrink: 0 }}
            >
              <InputLabel id="closing-add-budget-label">
                Adicionar orçamento
              </InputLabel>
              <Select
                labelId="closing-add-budget-label"
                label="Adicionar orçamento"
                value={addBudgetValue}
                onChange={(e) => {
                  const id = e.target.value as string;
                  if (id) {
                    onAddBudgetTab(id);
                    setAddBudgetValue("");
                  }
                }}
                sx={{ fontFamily: FONT, fontSize: 13, borderRadius: "6px" }}
              >
                <MenuItem value="">
                  <em>Escolher…</em>
                </MenuItem>
                {budgetsToAdd.map((b) => (
                  <MenuItem key={b._id} value={b._id}>
                    {comparisonBudgetDisplayName(b)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>
      </Box>

      {tab && (
        <Box
          sx={{
            px: 4,
            pt: 2,
            pb: 1,
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
          }}
        >
          <Tooltip
            title={
              !tabHasConsulting
                ? "Este orçamento não possui versão de assessoria"
                : ""
            }
          >
            <span>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={tab.includeConsulting}
                    onChange={(e) =>
                      onToggleConsulting(activeTab, e.target.checked)
                    }
                    disabled={tab.loading || !tabHasConsulting}
                    size="small"
                    sx={{
                      "& .MuiSvgIcon-root": { fontSize: 18, color: C.primary },
                    }}
                  />
                }
                label={
                  <Typography
                    sx={{
                      fontFamily: FONT,
                      fontSize: 14,
                      color: tabHasConsulting ? C.cellText : C.descText,
                    }}
                  >
                    Incluir versão assessoria
                  </Typography>
                }
                sx={{ m: 0, opacity: tabHasConsulting ? 1 : 0.55 }}
              />
            </span>
          </Tooltip>
        </Box>
      )}

      <DialogContent
        sx={{
          flex: 1,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          p: 0,
          px: 4,
          pb: 2,
        }}
      >
        {tab && (
          <>
            {tab.loading && <ComparisonTableSkeleton />}
            {!tab.loading && tab.error && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 2,
                  py: 6,
                }}
              >
                <Alert severity="error" sx={{ width: "100%" }}>
                  {tab.error}
                </Alert>
                <Button
                  variant="contained"
                  onClick={() => onRetry(activeTab)}
                  sx={{ bgcolor: C.primary, textTransform: "none" }}
                >
                  Tentar novamente
                </Button>
              </Box>
            )}
            {!tab.loading && !tab.error && tab.data && (
              <ClosingComparisonTable
                data={tab.data}
                collSec={tab.collSec}
                collCat={tab.collCat}
                onToggleSec={(sk) =>
                  updateTab(activeTab, {
                    collSec: tab.collSec.has(sk)
                      ? new Set(Array.from(tab.collSec).filter((x) => x !== sk))
                      : new Set([...Array.from(tab.collSec), sk]),
                  })
                }
                onToggleCat={(sKey, cKey) =>
                  updateTab(activeTab, {
                    collCat: tab.collCat.has(`${sKey}||${cKey}`)
                      ? new Set(
                          Array.from(tab.collCat).filter(
                            (x) => x !== `${sKey}||${cKey}`
                          )
                        )
                      : new Set([
                          ...Array.from(tab.collCat),
                          `${sKey}||${cKey}`,
                        ]),
                  })
                }
              />
            )}
            {!tab.loading && !tab.error && !tab.data && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flex: 1,
                }}
              >
                <CircularProgress size={32} sx={{ color: C.primary }} />
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <Box
        sx={{
          px: 4,
          py: 2,
          display: "flex",
          justifyContent: "flex-end",
          gap: 2,
          borderTop: bdr,
          flexShrink: 0,
        }}
      >
        <Button
          variant="outlined"
          onClick={onBack}
          sx={{
            fontFamily: FONT,
            fontWeight: 600,
            fontSize: 14,
            textTransform: "none",
            borderColor: C.primary,
            color: C.linkBlue,
            borderRadius: "8px",
            px: 1.5,
            height: 40,
          }}
        >
          Voltar
        </Button>
        <Button
          variant="contained"
          onClick={onClose}
          sx={{
            fontFamily: FONT,
            fontWeight: 600,
            fontSize: 14,
            textTransform: "none",
            bgcolor: C.primary,
            borderRadius: "8px",
            px: 1.5,
            height: 40,
            "&:hover": { bgcolor: "#0f4f89" },
          }}
        >
          Concluir
        </Button>
      </Box>
    </>
  );
};

const ComparisonTableSkeleton: React.FC = () => (
  <Box sx={{ flex: 1, overflow: "hidden", pt: 1 }}>
    {Array.from({ length: 8 }).map((_, i) => (
      <Skeleton
        key={i}
        variant="rectangular"
        height={i === 0 ? 48 : 32}
        sx={{ mb: 0.5, borderRadius: 1 }}
      />
    ))}
  </Box>
);

interface ClosingComparisonTableProps {
  data: BudgetInfoComparison[];
  collSec: Set<string>;
  collCat: Set<string>;
  onToggleSec: (sk: string) => void;
  onToggleCat: (sKey: string, cKey: string) => void;
}

const ClosingComparisonTable: React.FC<ClosingComparisonTableProps> = ({
  data,
  collSec,
  collCat,
  onToggleSec,
  onToggleCat,
}) => {
  const sortedData = useMemo(() => sortClosingCompareColumns(data), [data]);

  const comparisonTaxFlags = useMemo(
    () => getComparisonSummaryTaxFlags(sortedData),
    [sortedData]
  );
  const categoryTotalRows = useMemo(
    () => buildComparisonCategoryTotalRows(comparisonTaxFlags),
    [comparisonTaxFlags]
  );
  const tableRows = useMemo(
    () => buildRows(sortedData, categoryTotalRows),
    [sortedData, categoryTotalRows]
  );
  const firstSectionHeaderRowIndex = useMemo(
    () => tableRows.findIndex((r) => r.kind === "section-header"),
    [tableRows]
  );
  const nb = sortedData.length;
  const tableW = LABEL_W + sortedData.length * BUDGET_W_COLS + DIFF_TOTAL_W;
  const totalCols = 1 + sortedData.length * COL_WIDTHS.length + 1;

  return (
    <Box
      sx={{
        flex: 1,
        overflow: "auto",
        pt: 1,
        scrollbarWidth: "thin",
        "&::-webkit-scrollbar": { width: 6, height: 6 },
        "&::-webkit-scrollbar-thumb": {
          background: "rgba(0,0,0,0.2)",
          borderRadius: 3,
        },
      }}
    >
      {tableRows.length === 0 ? (
        <Box sx={{ p: 4, textAlign: "center" }}>
          <Typography sx={{ color: C.descText, fontFamily: FONT }}>
            Nenhum dado disponível para comparação.
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            display: "inline-block",
            ml: "12px",
            my: "8px",
            borderRadius: "10px",
            overflow: "clip",
            boxShadow: "0 0 0 1px rgba(0,0,0,0.10)",
          }}
        >
          <Box
            component="table"
            sx={{
              tableLayout: "fixed",
              borderCollapse: "collapse",
              width: tableW,
              fontFamily: FONT,
            }}
          >
            <colgroup>
              <col style={{ width: LABEL_W }} />
              {sortedData.flatMap((_, bi) =>
                COL_WIDTHS.map((w, ci) => (
                  <col key={`${bi}-${ci}`} style={{ width: w }} />
                ))
              )}
              <col style={{ width: DIFF_TOTAL_W }} />
            </colgroup>

            <Box component="thead">
              <Box component="tr">
                <Box
                  component="th"
                  sx={{
                    position: "sticky",
                    top: 0,
                    left: 0,
                    zIndex: 12,
                    bgcolor: C.white,
                    height: HEADER_H,
                    borderBottom: bdr,
                    borderRight: GROUP_DIVIDER_R,
                    boxShadow: "2px 0 4px rgba(0,0,0,0.04)",
                  }}
                />
                {sortedData.map((bd, bi) => (
                  <Box
                    component="th"
                    key={`${bd.budgetId}-${bd.version}`}
                    colSpan={COL_WIDTHS.length}
                    sx={{
                      position: "sticky",
                      top: 0,
                      zIndex: 10,
                      bgcolor: C.white,
                      height: HEADER_H,
                      borderTop: bdr,
                      borderBottom: bdr,
                      borderLeft: "none",
                      borderRight: GROUP_DIVIDER_R,
                      textAlign: "center",
                      fontSize: 14,
                      fontWeight: 700,
                      color: C.cellText,
                      fontFamily: FONT,
                      px: 0.5,
                    }}
                  >
                    {getClosingColLabel(bd)}
                  </Box>
                ))}
                <Box
                  component="th"
                  sx={{
                    position: "sticky",
                    top: 0,
                    zIndex: 10,
                    bgcolor: "#e8f5e9",
                    height: HEADER_H,
                    borderTop: bdr,
                    borderBottom: bdr,
                    borderLeft: "none",
                    borderRight: bdr,
                    textAlign: "center",
                    fontSize: 13,
                    fontWeight: 700,
                    color: C.savingsPos,
                    fontFamily: FONT,
                    whiteSpace: "nowrap",
                  }}
                >
                  Diferença Total
                </Box>
              </Box>
            </Box>

            <Box component="tbody">
              {tableRows.map((row, ri) => {
                if (!isRowVisible(row, collSec, collCat)) return null;

                if (row.kind === "info-row") {
                  const isFirst = ri === 0;
                  return (
                    <Box component="tr" key={ri}>
                      <Box
                        component="td"
                        sx={{
                          position: "sticky",
                          left: 0,
                          zIndex: 2,
                          bgcolor: C.categoryBg,
                          borderTop: isFirst ? bdr : undefined,
                          borderBottom: bdr,
                          height: 28,
                          pl: "14px",
                          pr: "10px",
                          fontSize: 12,
                          fontWeight: 700,
                          color: C.cellText,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          borderRight: GROUP_DIVIDER_R,
                        }}
                      >
                        {row.label}
                      </Box>
                      {sortedData.map((bd) => (
                        <Box
                          component="td"
                          key={`${bd.budgetId}-${bd.version}`}
                          colSpan={COL_WIDTHS.length}
                          sx={{
                            bgcolor: C.categoryBg,
                            borderTop: isFirst ? bdr : undefined,
                            borderBottom: bdr,
                            borderRight: GROUP_DIVIDER_R,
                            height: 28,
                            pl: "14px",
                            pr: "10px",
                            fontSize: 12,
                            color: C.cellText,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {getInfoValue(bd, row.fieldKey)}
                        </Box>
                      ))}
                      <Box
                        component="td"
                        sx={{
                          bgcolor: "rgba(46,125,50,0.06)",
                          borderTop: isFirst ? bdr : undefined,
                          borderBottom: bdr,
                          borderRight: bdr,
                          height: 28,
                        }}
                      />
                    </Box>
                  );
                }

                if (row.kind === "gap") {
                  return (
                    <Box component="tr" key={ri} sx={{ height: row.height }}>
                      <Box
                        component="td"
                        colSpan={totalCols}
                        sx={{ bgcolor: C.white }}
                      />
                    </Box>
                  );
                }

                if (row.kind === "section-header") {
                  const expanded = !collSec.has(row.sectionKey);
                  const sectionStageDivider =
                    firstSectionHeaderRowIndex >= 0 &&
                    ri > firstSectionHeaderRowIndex
                      ? GROUP_DIVIDER_R
                      : undefined;
                  return (
                    <Box component="tr" key={ri}>
                      <Box
                        component="td"
                        onClick={() => onToggleSec(row.sectionKey)}
                        sx={{
                          position: "sticky",
                          left: 0,
                          zIndex: 2,
                          bgcolor: C.sectionBg,
                          cursor: "pointer",
                          pl: "10px",
                          pr: "8px",
                          py: "10px",
                          verticalAlign: "middle",
                          borderRight: SECTION_INNER_R,
                          borderTop: sectionStageDivider,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                            minWidth: 0,
                          }}
                        >
                          <MdKeyboardArrowDown
                            size={18}
                            color={C.white}
                            style={{
                              flexShrink: 0,
                              transform: expanded ? "none" : "rotate(-90deg)",
                              transition: "transform 0.2s",
                            }}
                          />
                          <Typography
                            sx={{
                              fontSize: 13,
                              fontWeight: 700,
                              color: C.white,
                              whiteSpace: "normal",
                              wordBreak: "break-word",
                              fontFamily: FONT,
                            }}
                          >
                            {row.label}
                          </Typography>
                        </Box>
                      </Box>
                      {sortedData.flatMap((bd, bi) => {
                        const vals = getValues(
                          bd,
                          row,
                          sortedData,
                          bi,
                          comparisonTaxFlags
                        );
                        return COL_WIDTHS.map((_, ci) => {
                          const isSavings = ci === IDX_SAVINGS;
                          const show = ci === IDX_TOTAL || isSavings;
                          const isLastInGroup = ci === IDX_OBS;
                          return (
                            <Box
                              component="td"
                              key={`${bi}-${ci}`}
                              sx={{
                                bgcolor: C.sectionBg,
                                textAlign: "right",
                                pr: "10px",
                                pl: "10px",
                                py: "10px",
                                verticalAlign: "middle",
                                fontSize: 13,
                                fontWeight: 700,
                                color: show ? C.white : C.sectionBg,
                                whiteSpace: "nowrap",
                                borderRight: isLastInGroup
                                  ? GROUP_DIVIDER_R
                                  : SECTION_INNER_R,
                                borderTop: sectionStageDivider,
                              }}
                            >
                              {show ? String(vals[ci]) : ""}
                            </Box>
                          );
                        });
                      })}
                      {(() => {
                        const savingTotal = sumSavingsForDiffTotal(
                          sortedData,
                          row,
                          comparisonTaxFlags
                        );
                        return (
                          <Box
                            component="td"
                            sx={{
                              bgcolor: C.sectionBg,
                              textAlign: "right",
                              pr: "10px",
                              pl: "10px",
                              py: "10px",
                              verticalAlign: "middle",
                              fontSize: 13,
                              fontWeight: 700,
                              color: C.white,
                              whiteSpace: "nowrap",
                              borderRight: bdr,
                              borderTop: sectionStageDivider,
                            }}
                          >
                            {savingTotal !== 0 ? fmtCurrency(savingTotal) : "-"}
                          </Box>
                        );
                      })()}
                    </Box>
                  );
                }

                if (row.kind === "category-header") {
                  const catKey = `${row.sectionKey}||${row.categoryKey}`;
                  const expanded = !collCat.has(catKey);
                  return (
                    <Box
                      component="tr"
                      key={ri}
                      sx={{ height: ROW_H["category-header"] }}
                    >
                      <Box
                        component="td"
                        colSpan={totalCols}
                        onClick={() =>
                          onToggleCat(row.sectionKey, row.categoryKey)
                        }
                        sx={{
                          position: "sticky",
                          left: 0,
                          bgcolor: C.categoryBg,
                          cursor: "pointer",
                          borderTop: bdr,
                          borderBottom: bdr,
                          pl: "12px",
                          pr: "8px",
                          overflow: "hidden",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                          }}
                        >
                          {expanded ? (
                            <MdKeyboardArrowUp
                              size={16}
                              color={C.cellText}
                              style={{ flexShrink: 0 }}
                            />
                          ) : (
                            <MdKeyboardArrowDown
                              size={16}
                              color={C.cellText}
                              style={{ flexShrink: 0 }}
                            />
                          )}
                          <Typography
                            sx={{
                              fontSize: 13,
                              fontWeight: 700,
                              color: C.cellText,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              fontFamily: FONT,
                            }}
                          >
                            {row.label}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  );
                }

                if (row.kind === "column-header") {
                  return (
                    <Box
                      component="tr"
                      key={ri}
                      sx={{ height: ROW_H["column-header"] }}
                    >
                      <Box
                        component="td"
                        sx={{
                          position: "sticky",
                          left: 0,
                          zIndex: 2,
                          bgcolor: C.categoryBg,
                          borderBottom: bdr,
                          borderRight: GROUP_DIVIDER_R,
                        }}
                      />
                      {sortedData.flatMap((bd, bi) =>
                        COL_WIDTHS.map((_, ci) => {
                          const isSavings = ci === IDX_SAVINGS;
                          const isDescricao = ci === IDX_DESCRICAO;
                          const isObs = ci === IDX_OBS;
                          const label = isObs
                            ? OBS_LABEL
                            : isDescricao
                            ? "Descrição"
                            : ci >= 1 && ci <= 4
                            ? COL_LABELS_BASE[ci - 1]
                            : savingsSubcolumnLabel(bd);
                          return (
                            <Box
                              component="td"
                              key={`${bi}-${ci}`}
                              sx={{
                                bgcolor: isSavings
                                  ? "rgba(46,125,50,0.06)"
                                  : C.categoryBg,
                                borderBottom: bdr,
                                borderRight: budgetBlockCellBorderRight(ci),
                                textAlign:
                                  isObs || isDescricao ? "left" : "right",
                                pr: "10px",
                                pl: "10px",
                                fontSize: 12,
                                fontWeight: 700,
                                color: isSavings ? C.savingsPos : C.cellText,
                                whiteSpace: "nowrap",
                              }}
                            >
                              {label}
                            </Box>
                          );
                        })
                      )}
                      <Box
                        component="td"
                        sx={{
                          bgcolor: "#e8f5e9",
                          borderBottom: bdr,
                          borderRight: bdr,
                          textAlign: "right",
                          pr: "10px",
                          pl: "10px",
                          fontSize: 12,
                          fontWeight: 700,
                          color: C.savingsPos,
                          whiteSpace: "nowrap",
                        }}
                      >
                        Diferença Total
                      </Box>
                    </Box>
                  );
                }

                if (row.kind === "grand-total") {
                  return (
                    <Box component="tr" key={ri}>
                      <Box
                        component="td"
                        sx={{
                          position: "sticky",
                          left: 0,
                          zIndex: 2,
                          bgcolor: C.grandTotalBg,
                          pl: "12px",
                          pr: "8px",
                          py: "10px",
                          verticalAlign: "middle",
                          borderRight: SECTION_INNER_R,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: 13,
                            fontWeight: 700,
                            color: C.white,
                            whiteSpace: "normal",
                            wordBreak: "break-word",
                            fontFamily: FONT,
                          }}
                        >
                          {row.label}
                        </Typography>
                      </Box>
                      {sortedData.flatMap((bd, bi) => {
                        const vals = getValues(
                          bd,
                          row,
                          sortedData,
                          bi,
                          comparisonTaxFlags
                        );
                        return COL_WIDTHS.map((_, ci) => {
                          const isSavings = ci === IDX_SAVINGS;
                          const isTotal = ci === IDX_TOTAL;
                          const show = isSavings || isTotal;
                          return (
                            <Box
                              component="td"
                              key={`${bi}-${ci}`}
                              sx={{
                                bgcolor: C.grandTotalBg,
                                textAlign: "right",
                                pr: "10px",
                                pl: "10px",
                                py: "10px",
                                verticalAlign: "middle",
                                fontSize: 13,
                                fontWeight: 700,
                                color: show ? C.white : C.grandTotalBg,
                                whiteSpace: "nowrap",
                                borderRight: budgetSectionInnerBorderRight(ci),
                              }}
                            >
                              {show ? String(vals[ci]) : ""}
                            </Box>
                          );
                        });
                      })}
                      {(() => {
                        const savingTotal = sumSavingsForDiffTotal(
                          sortedData,
                          row,
                          comparisonTaxFlags
                        );
                        return (
                          <Box
                            component="td"
                            sx={{
                              bgcolor: C.grandTotalBg,
                              textAlign: "right",
                              pr: "10px",
                              pl: "10px",
                              py: "10px",
                              verticalAlign: "middle",
                              fontSize: 13,
                              fontWeight: 700,
                              color: C.white,
                              whiteSpace: "nowrap",
                              borderRight: bdr,
                            }}
                          >
                            {savingTotal !== 0 ? fmtCurrency(savingTotal) : "-"}
                          </Box>
                        );
                      })()}
                    </Box>
                  );
                }

                if (row.kind === "separator") {
                  return (
                    <Box component="tr" key={ri} sx={{ height: 4 }}>
                      <Box
                        component="td"
                        sx={{
                          position: "sticky",
                          left: 0,
                          zIndex: 2,
                          bgcolor: C.separatorBg,
                          borderRight: GROUP_DIVIDER_R,
                        }}
                      />
                      {sortedData.flatMap((_, bi) =>
                        COL_WIDTHS.map((__, ci) => (
                          <Box
                            component="td"
                            key={`${bi}-${ci}`}
                            sx={{
                              bgcolor: C.separatorBg,
                              borderRight: budgetBlockCellBorderRight(ci),
                            }}
                          />
                        ))
                      )}
                      <Box component="td" sx={{ bgcolor: C.separatorBg }} />
                    </Box>
                  );
                }

                const isCatTotal = row.kind === "category-total";
                const isSecTotal = row.kind === "section-total";
                const isLastCatTotal =
                  isCatTotal && (row as { isLast?: boolean }).isLast;
                const rowBg = isSecTotal ? C.categoryBg : C.white;

                const savingTotal = sumSavingsForDiffTotal(
                  sortedData,
                  row,
                  comparisonTaxFlags
                );
                const savingTotalStr =
                  savingTotal !== 0 ? fmtCurrency(savingTotal) : "-";
                const hasSavingTotal = savingTotal !== 0;
                const savingTotalColor =
                  savingTotal > 0 ? C.savingsNeg : C.savingsPos;
                const savingTotalBg =
                  savingTotal > 0
                    ? "rgba(211,47,47,0.05)"
                    : "rgba(46,125,50,0.05)";

                return (
                  <Box component="tr" key={ri} sx={{ height: ROW_H[row.kind] }}>
                    <Box
                      component="td"
                      sx={{
                        position: "sticky",
                        left: 0,
                        zIndex: 2,
                        bgcolor: rowBg,
                        pl: "14px",
                        pr: "10px",
                        fontSize: 13,
                        fontFamily: FONT,
                        color: C.cellText,
                        fontWeight: isSecTotal ? 700 : 400,
                        borderBottom: isLastCatTotal ? "none" : bdr,
                        borderTop: isSecTotal ? bdr : "none",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        borderRight: GROUP_DIVIDER_R,
                      }}
                    >
                      {(row as { label?: string }).label}
                    </Box>
                    {(() => {
                      const baseBd = sortedData.find((d) => d.version === 0);
                      const baseTotal = baseBd
                        ? getTotalRaw(baseBd, row, comparisonTaxFlags)
                        : 0;
                      return sortedData.flatMap((bd, bi) => {
                        const vals = getValues(
                          bd,
                          row,
                          sortedData,
                          bi,
                          comparisonTaxFlags
                        );
                        const rawSavings = getSavingsRaw(
                          sortedData,
                          bi,
                          row,
                          comparisonTaxFlags
                        );
                        const rawTotal = getTotalRaw(
                          bd,
                          row,
                          comparisonTaxFlags
                        );
                        return COL_WIDTHS.map((_, ci) => {
                          const isSavings = ci === IDX_SAVINGS;
                          const isTotal = ci === IDX_TOTAL;
                          const isDescricao = ci === IDX_DESCRICAO;
                          const isObs = ci === IDX_OBS;
                          const cellVal = vals[ci];
                          let cellStyle: { color: string; bgcolor: string };
                          if (isSavings) {
                            cellStyle = savingsStyle(rawSavings);
                          } else if (isTotal && bd.version !== 0) {
                            cellStyle = totalStyle(rawTotal, baseTotal);
                          } else {
                            cellStyle = { color: C.cellText, bgcolor: rowBg };
                          }
                          const hasHighlight =
                            cellStyle.bgcolor !== "transparent" &&
                            cellStyle.bgcolor !== rowBg;
                          const cellSx: any = {
                            bgcolor:
                              isSavings || (isTotal && bd.version !== 0)
                                ? cellStyle.bgcolor
                                : rowBg,
                            textAlign: isObs || isDescricao ? "left" : "right",
                            pr: "10px",
                            pl: "10px",
                            fontSize: 13,
                            fontFamily: FONT,
                            color: cellStyle.color,
                            fontWeight: isSecTotal || hasHighlight ? 700 : 400,
                            borderRight: budgetBlockCellBorderRight(ci),
                            borderBottom: isLastCatTotal ? "none" : bdr,
                            borderTop: isSecTotal ? bdr : "none",
                            whiteSpace: "nowrap",
                          };
                          if (isObs || isDescricao) {
                            return (
                              <ObsCell
                                key={`${bi}-${ci}`}
                                value={String(cellVal)}
                                baseSx={cellSx}
                              />
                            );
                          }
                          return (
                            <Box component="td" key={`${bi}-${ci}`} sx={cellSx}>
                              {String(cellVal)}
                            </Box>
                          );
                        });
                      });
                    })()}
                    <Box
                      component="td"
                      sx={{
                        bgcolor: savingTotalBg,
                        textAlign: "right",
                        pr: "10px",
                        pl: "10px",
                        fontSize: 13,
                        fontFamily: FONT,
                        color: savingTotalColor,
                        fontWeight: isSecTotal || hasSavingTotal ? 700 : 400,
                        borderBottom: isLastCatTotal ? "none" : bdr,
                        borderTop: isSecTotal ? bdr : "none",
                        borderRight: bdr,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {savingTotalStr}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ClosingComparisonModal;
